import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Check, Users, ArrowRight, Briefcase, Shield } from "lucide-react";

const CorporateMemberships = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const plans = [
    {
      title: "Corporate Membership #1",
      cost: "$6,000",
      costNote: "taxes included",
      description:
        "The membership is set up so that one person is actually a member. The Company may submit a list of 4 additional employees that have the capability to bring 1 foursome out per day at no charge, excluding league times. The membership includes cart privileges as well as golf and driving range access.",
      note: "Any additional play on the same day will be charged at a rate of $35 walking and $50 with cart (tax included).",
      highlights: [
        "1 Primary Member",
        "Up to 4 additional employees listed",
        "1 Foursome per day at no charge",
        "Cart privileges included",
        "Golf & driving range access",
        "Excludes league times",
      ],
    },
    {
      title: "Corporate Membership #2",
      cost: "$6,000",
      costNote: "taxes included",
      description:
        "The membership is set up so that one person is actually a member. The Company may submit a list of up to 19 additional employees that have the capability to play at no charge. This is limited to one tee time per day, excluding league times. The membership includes cart privileges as well as golf and driving range access. Only the primary member will have the ability to play in leagues and club events.",
      note: "Any golfers playing within the tee time that are not on the employee list will be charged at a rate of $35 walking and $50 riding (tax included).",
      highlights: [
        "1 Primary Member",
        "Up to 19 additional employees listed",
        "1 Tee time per day at no charge",
        "Cart privileges included",
        "Golf & driving range access",
        "Only primary member eligible for leagues",
      ],
    },
  ];

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[450px] flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url(https://images.unsplash.com/photo-1616993020848-4c1d782d430a?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85)" }}
        >
          <div className="absolute inset-0 bg-[#1B2A4A]/75" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full">
          <span className="text-[#C9A96E] text-sm font-medium tracking-[0.2em] uppercase">For Business</span>
          <h1 className="text-5xl md:text-6xl font-serif font-bold text-white mt-3">2026 Corporate Golf Memberships</h1>
          <p className="text-white/70 text-lg mt-4 max-w-2xl">
            Reward your team and entertain clients on one of the finest courses in Southern Ontario.
          </p>
        </div>
      </section>

      {/* Plans */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <span className="text-[#C9A96E] text-sm font-medium tracking-[0.2em] uppercase">Pricing</span>
            <h2 className="text-4xl font-serif font-bold text-[#1B2A4A] mt-3 mb-4">Corporate Membership Options</h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              Both corporate memberships are priced at $6,000 (taxes included), offering flexible options to suit your company's needs.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {plans.map((plan, i) => (
              <div key={i} className={`rounded-2xl overflow-hidden shadow-sm border flex flex-col ${i === 0 ? "border-gray-100" : "border-[#C9A96E]/30"}`}>
                <div className={`px-8 py-6 ${i === 0 ? "bg-[#f8f7f4]" : "bg-[#1B2A4A]"}`}>
                  <div className="flex items-center justify-between">
                    <h3 className={`text-xl font-serif font-bold ${i === 0 ? "text-[#1B2A4A]" : "text-white"}`}>{plan.title}</h3>
                    <Briefcase size={22} className={i === 0 ? "text-[#C9A96E]" : "text-[#C9A96E]"} />
                  </div>
                  <div className={`mt-3 text-3xl font-bold ${i === 0 ? "text-[#1B2A4A]" : "text-white"}`}>
                    {plan.cost} <span className={`text-sm font-normal ${i === 0 ? "text-gray-400" : "text-white/50"}`}>{plan.costNote}</span>
                  </div>
                </div>
                <div className="bg-white px-8 py-6 flex flex-col flex-1">
                  <p className="text-gray-600 text-sm leading-relaxed mb-5">{plan.description}</p>
                  <ul className="space-y-2 mb-5 flex-1">
                    {plan.highlights.map((h, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-gray-700">
                        <Check size={14} className="text-[#C9A96E] mt-0.5 shrink-0" /> {h}
                      </li>
                    ))}
                  </ul>
                  <div className="bg-[#f8f7f4] rounded-lg p-4 text-xs text-gray-500 leading-relaxed">
                    {plan.note}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Proof of employment note */}
          <div className="flex items-start gap-4 bg-[#C9A96E]/10 border border-[#C9A96E]/30 rounded-xl p-5 max-w-3xl mx-auto">
            <Shield size={20} className="text-[#C9A96E] shrink-0 mt-0.5" />
            <p className="text-sm text-gray-600">
              <strong>Please note:</strong> Proof of corporate employment status will need to be provided upon request from the corporate member's organizer.
            </p>
          </div>
        </div>
      </section>

      {/* Why Corporate Golf */}
      <section className="py-20 bg-[#f8f7f4]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#C9A96E] text-sm font-medium tracking-[0.2em] uppercase">Why Willow Ridge</span>
              <h2 className="text-4xl font-serif font-bold text-[#1B2A4A] mt-3 mb-6">The Perfect Setting for Business</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Willow Ridge Golf & Country Club offers a premier corporate golf experience in a scenic, relaxed environment. Whether you're rewarding your team or entertaining clients, our course delivers an exceptional round every time.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Users, label: "Team Building", desc: "Strengthen business relationships on the fairway" },
                  { icon: Briefcase, label: "Client Entertainment", desc: "Impress clients with a world-class experience" },
                  { icon: Check, label: "Cart Included", desc: "Both packages include full cart privileges" },
                  { icon: Shield, label: "Exclusive Access", desc: "Priority booking for corporate members" },
                ].map(({ icon: Icon, label, desc }, i) => (
                  <div key={i} className="bg-white rounded-xl p-4 shadow-sm">
                    <Icon size={18} className="text-[#C9A96E] mb-2" />
                    <div className="text-sm font-semibold text-[#1B2A4A] mb-0.5">{label}</div>
                    <div className="text-xs text-gray-500">{desc}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img
                src="https://images.unsplash.com/photo-1629673120178-53a664eec9e8?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
                alt="Corporate golf group"
                className="w-full h-[420px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#1B2A4A]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4">
            Interested in a Corporate Membership?
          </h2>
          <p className="text-white/60 mb-8">
            Contact us to discuss options, arrange a tour, or get your corporate membership started for the 2026 season.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#C9A96E] text-[#1B2A4A] font-semibold rounded-md hover:bg-[#d4b97e] transition-colors"
            >
              Get in Touch <ArrowRight size={18} />
            </Link>
            <a
              href="tel:5196768772"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/30 text-white font-semibold rounded-md hover:bg-white/10 transition-colors"
            >
              Call (519) 676-8772
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CorporateMemberships;
